/**
 * Simplified vehicle list for mobile devices.
 * Shows: Line badge, Direction, Last updated.
 */

import { useMemo, useCallback, useState, useEffect } from 'react';

import { useVehicleStore } from '../../stores/vehicleStore';
import { useFilterStore } from '../../stores/filterStore';
import { formatDataAge } from '../../utils/confidence';

export interface MobileVehicleListProps {
  className?: string;
}

export function MobileVehicleList({ className = '' }: MobileVehicleListProps) {
  const vehicles = useVehicleStore((state) => state.vehicles);
  const selectedVehicleId = useVehicleStore((state) => state.selectedVehicleId);
  const selectVehicle = useVehicleStore((state) => state.selectVehicle);
  const enabledModes = useFilterStore((state) => state.enabledModes);
  const zoomLevel = useFilterStore((state) => state.zoomLevel);
  const lineFilters = useFilterStore((state) => state.lineFilters);
  const getVisibleModes = useFilterStore((state) => state.getVisibleModes);
  const hasLineFilter = useFilterStore((state) => state.hasLineFilter);

  // Shared timer for freshness display
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Get visible transport modes
  const visibleModes = useMemo(
    () => getVisibleModes(),
    [enabledModes, zoomLevel, getVisibleModes]
  );

  // Filter and sort vehicles
  const sortedVehicles = useMemo(() => {
    const filtered = Array.from(vehicles.values()).filter((v) => {
      if (!v.line || !visibleModes.includes(v.line.transport_mode)) {
        return false;
      }
      if (v.visualState === 'dead') {
        return false;
      }
      const lineName = v.line?.short_name ?? '';
      if (!hasLineFilter(lineName)) {
        return false;
      }
      return true;
    });

    // Sort by last update time (most recent first)
    return filtered.sort((a, b) => b.lastUpdateTime - a.lastUpdateTime);
  }, [vehicles, visibleModes, lineFilters, hasLineFilter]);

  const handleRowClick = useCallback(
    (id: string) => {
      selectVehicle(id === selectedVehicleId ? null : id);
    },
    [selectVehicle, selectedVehicleId]
  );

  if (sortedVehicles.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full p-8 text-gray-500 dark:text-gray-400 ${className}`}>
        <p className="text-center">No vehicles to display. Adjust your filters or wait for data.</p>
      </div>
    );
  }

  return (
    <div className={`h-full overflow-y-auto bg-white dark:bg-gray-800 ${className}`}>
      <ul className="divide-y divide-gray-200 dark:divide-gray-700" role="listbox">
        {sortedVehicles.map((vehicle) => (
          <li
            key={vehicle.service_journey_id}
            onClick={() => handleRowClick(vehicle.service_journey_id)}
            className={`
              flex items-center gap-3 px-4 py-3 min-h-[56px] cursor-pointer
              active:bg-gray-100 dark:active:bg-gray-700 transition-colors
              ${selectedVehicleId === vehicle.service_journey_id
                ? 'bg-blue-50 dark:bg-blue-900/30'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}
            `}
            role="option"
            aria-selected={selectedVehicleId === vehicle.service_journey_id}
          >
            {/* Line badge */}
            <div
              className="flex-shrink-0 w-12 h-8 flex items-center justify-center rounded-full text-sm font-bold"
              style={{
                backgroundColor: vehicle.line?.color_bg || '#666666',
                color: vehicle.line?.color_fg || '#ffffff',
              }}
            >
              {vehicle.line?.short_name ?? '?'}
            </div>

            {/* Direction */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {vehicle.direction || 'Unknown direction'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {vehicle.data_source === 'v1-da-gps' ? '📡 GPS' : '📍 Estimated'}
              </p>
            </div>

            {/* Last updated */}
            <div className="flex-shrink-0 text-right">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatDataAge(vehicle.lastUpdateTime, currentTime)}
              </p>
            </div>

            {/* Selection indicator */}
            <svg
              className={`w-5 h-5 flex-shrink-0 text-gray-400 ${
                selectedVehicleId === vehicle.service_journey_id ? 'text-blue-600' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </li>
        ))}
      </ul>

      {/* Vehicle count */}
      <div className="sticky bottom-0 px-4 py-2 bg-gray-50 dark:bg-gray-900 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
        {sortedVehicles.length} vehicle{sortedVehicles.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
